import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Search,
  Filter,
  Eye,
  Trash2,
  CheckCircle,
  Clock,
  Archive,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Download,
  User,
  Calendar,
  MessageSquare
} from 'lucide-react';
import { UserRole } from '../types';

interface CaseData {
  id: string;
  title: string;
  description: string;
  case_type: string;
  urgency_level: 'low' | 'normal' | 'high' | 'urgent';
  status: 'open' | 'resolved' | 'archived' | 'deleted';
  created_at: string;
  updated_at: string;
  view_count: number;
  comment_count: number;
  first_name: string;
  last_name: string;
  email: string;
  role: UserRole;
  hospital_affiliation?: string;
}

interface CaseFilters {
  search: string;
  status: string;
  case_type: string;
  urgency_level: string;
  page: number;
  limit: number;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  type: 'danger' | 'warning' | 'info';
  confirmText?: string;
  cancelText?: string;
}

function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type,
  confirmText = 'Επιβεβαίωση',
  cancelText = 'Ακύρωση'
}: ConfirmationModalProps) {
  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: <AlertTriangle className="w-6 h-6 text-red-400" />,
          button: 'bg-red-600 hover:bg-red-700 text-white'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          icon: <AlertTriangle className="w-6 h-6 text-yellow-400" />,
          button: 'bg-yellow-600 hover:bg-yellow-700 text-white'
        };
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          icon: <CheckCircle className="w-6 h-6 text-blue-400" />,
          button: 'bg-blue-600 hover:bg-blue-700 text-white'
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className={`flex items-center p-6 ${styles.bg} ${styles.border} border-b rounded-t-lg`}>
          {styles.icon}
          <h3 className="ml-3 text-lg font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="p-6">
          <p className="text-gray-700">{message}</p>
        </div>
        <div className="flex justify-end space-x-3 p-6 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded-md transition-colors ${styles.button}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export function CaseManagement() {
  const navigate = useNavigate();
  const [cases, setCases] = useState<CaseData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCase, setSelectedCase] = useState<CaseData | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState<any>({});
  
  const [filters, setFilters] = useState<CaseFilters>({
    search: '',
    status: '',
    case_type: '',
    urgency_level: '',
    page: 1,
    limit: 20
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
    hasNext: false,
    hasPrevious: false
  });

  useEffect(() => {
    fetchCases();
  }, [filters]);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      
      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.set('search', filters.search);
      if (filters.status) queryParams.set('status', filters.status);
      if (filters.case_type) queryParams.set('case_type', filters.case_type);
      if (filters.urgency_level) queryParams.set('urgency_level', filters.urgency_level);
      queryParams.set('page', filters.page.toString());
      queryParams.set('limit', filters.limit.toString());

      const response = await fetch(`/api/v1/admin/cases?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Αποτυχία λήψης περιστατικών');
      }

      const result = await response.json();
      setCases(result.data.cases);
      setPagination(result.data.pagination);
    } catch (error) {
      console.error('Fetch cases error:', error);
      setError(error instanceof Error ? error.message : 'Άγνωστο σφάλμα');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCase = async (reason: string = '') => {
    if (!selectedCase) return;

    try {
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`/api/v1/admin/cases/${selectedCase.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (!response.ok) {
        throw new Error('Αποτυχία διαγραφής περιστατικού');
      }

      // Update local state
      setCases(prev => prev.map(case_ => 
        case_.id === selectedCase.id 
          ? { ...case_, status: 'deleted' as const }
          : case_
      ));

      setShowConfirmModal(false);
      setSelectedCase(null);
    } catch (error) {
      console.error('Delete case error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα διαγραφής περιστατικού');
    }
  };

  const handleResolveCase = async () => {
    if (!selectedCase) return;

    try {
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`/api/v1/admin/cases/${selectedCase.id}/resolve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          reason: `Επιλύθηκε από διαχειριστή στις ${new Date().toLocaleDateString('el-GR')}`,
          resolution: 'Επιλύθηκε από διαχειριστή'
        })
      });

      if (!response.ok) {
        throw new Error('Αποτυχία επίλυσης περιστατικού');
      }

      // Update local state
      setCases(prev => prev.map(case_ => 
        case_.id === selectedCase.id 
          ? { ...case_, status: 'resolved' as const }
          : case_
      ));

      setShowConfirmModal(false);
      setSelectedCase(null);
    } catch (error) {
      console.error('Resolve case error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα επίλυσης περιστατικού');
    }
  };

  // Closure-based handler functions that accept the case directly
  const handleDeleteCaseById = async (case_: CaseData, reason: string = '') => {
    console.log('[DEBUG] CaseManagement handleDeleteCaseById called with case:', case_.id);
    try {
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`/api/v1/admin/cases/${case_.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (!response.ok) {
        throw new Error('Αποτυχία διαγραφής περιστατικού');
      }

      // Update local state
      setCases(prev => prev.map(c => 
        c.id === case_.id 
          ? { ...c, status: 'deleted' as const }
          : c
      ));

      setShowConfirmModal(false);
      setSelectedCase(null);
    } catch (error) {
      console.error('Delete case error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα διαγραφής περιστατικού');
    }
  };

  const handleResolveCaseById = async (case_: CaseData) => {
    console.log('[DEBUG] CaseManagement handleResolveCaseById called with case:', case_.id);
    try {
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`/api/v1/admin/cases/${case_.id}/resolve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          reason: `Επιλύθηκε από διαχειριστή στις ${new Date().toLocaleDateString('el-GR')}`,
          resolution: 'Επιλύθηκε από διαχειριστή'
        })
      });

      if (!response.ok) {
        throw new Error('Αποτυχία επίλυσης περιστατικού');
      }

      // Update local state
      setCases(prev => prev.map(c => 
        c.id === case_.id 
          ? { ...c, status: 'resolved' as const }
          : c
      ));

      setShowConfirmModal(false);
      setSelectedCase(null);
    } catch (error) {
      console.error('Resolve case error:', error);
      setError(error instanceof Error ? error.message : 'Σφάλμα επίλυσης περιστατικού');
    }
  };

  const openConfirmModal = (case_: CaseData, action: string) => {
    setSelectedCase(case_);

    // Create handler functions that capture the current case and action
    const handleConfirmAction = () => {
      console.log('[DEBUG] CaseManagement handleConfirmAction called with action:', action, 'case:', case_.id);
      switch (action) {
        case 'delete':
          console.log('[DEBUG] Calling handleDeleteCaseById()');
          handleDeleteCaseById(case_);
          break;
        case 'resolve':
          console.log('[DEBUG] Calling handleResolveCaseById()');
          handleResolveCaseById(case_);
          break;
        default:
          console.log('[DEBUG] Unknown action:', action);
      }
    };

    let config = {};
    switch (action) {
      case 'delete':
        config = {
          title: 'Διαγραφή Περιστατικού',
          message: `Είστε σίγουροι ότι θέλετε να διαγράψετε το περιστατικό "${case_.title}"; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.`,
          type: 'danger',
          confirmText: 'Διαγραφή',
          onConfirm: handleConfirmAction
        };
        break;
      case 'resolve':
        config = {
          title: 'Επίλυση Περιστατικού',
          message: `Είστε σίγουροι ότι θέλετε να επιλύσετε το περιστατικό "${case_.title}";`,
          type: 'info',
          confirmText: 'Επίλυση',
          onConfirm: handleConfirmAction
        };
        break;
    }

    setConfirmConfig(config);
    setShowConfirmModal(true);
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Clock className="w-3 h-3 mr-1" />
            Ανοιχτό
          </span>
        );
      case 'resolved':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Επιλυμένο
          </span>
        );
      case 'archived':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <Archive className="w-3 h-3 mr-1" />
            Αρχειοθετημένο
          </span>
        );
      case 'deleted':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            Διαγραμμένο
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  const getUrgencyBadge = (urgency: string) => {
    switch (urgency) {
      case 'urgent':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            🚨 Επείγον
          </span>
        );
      case 'high':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            ⚡ Υψηλή
          </span>
        );
      case 'normal':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            📋 Κανονική
          </span>
        );
      case 'low':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            📚 Εκπαιδευτικό
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {urgency}
          </span>
        );
    }
  };

  const getCaseTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      'biopsy': 'Βιοψία',
      'cytology': 'Κυττολογία',
      'frozen_section': 'Κατεψυγμένη Τομή', 
      'autopsy': 'Νεκροψία',
      'consultation': 'Σύμβουλος'
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <XCircle className="w-5 h-5 text-red-400 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Σφάλμα φόρτωσης</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={fetchCases}
          className="mt-4 bg-red-100 hover:bg-red-200 text-red-800 px-4 py-2 rounded-md text-sm font-medium transition-colors"
        >
          Δοκιμή ξανά
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Διαχείριση Περιστατικών</h1>
            <p className="text-gray-600 mt-1">
              Επισκόπηση και διαχείριση {pagination.total.toLocaleString()} περιστατικών
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={fetchCases}
              className="flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Ανανέωση
            </button>
            <button className="flex items-center px-3 py-2 bg-medical-600 hover:bg-medical-700 text-white rounded-md transition-colors">
              <Download className="w-4 h-4 mr-2" />
              Εξαγωγή
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Αναζήτηση
            </label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                placeholder="Τίτλος, περιγραφή..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Κατάσταση
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Όλες οι καταστάσεις</option>
              <option value="open">Ανοιχτά</option>
              <option value="resolved">Επιλυμένα</option>
              <option value="archived">Αρχειοθετημένα</option>
              <option value="deleted">Διαγραμμένα</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Τύπος Περιστατικού
            </label>
            <select
              value={filters.case_type}
              onChange={(e) => setFilters(prev => ({ ...prev, case_type: e.target.value, page: 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Όλοι οι τύποι</option>
              <option value="biopsy">Βιοψία</option>
              <option value="cytology">Κυττολογία</option>
              <option value="frozen_section">Κατεψυγμένη Τομή</option>
              <option value="autopsy">Νεκροψία</option>
              <option value="consultation">Σύμβουλος</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Επιπεδο Επείγοντος
            </label>
            <select
              value={filters.urgency_level}
              onChange={(e) => setFilters(prev => ({ ...prev, urgency_level: e.target.value, page: 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Όλα τα επίπεδα</option>
              <option value="urgent">Επείγον</option>
              <option value="high">Υψηλή</option>
              <option value="normal">Κανονική</option>
              <option value="low">Εκπαιδευτικό</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ανά σελίδα
            </label>
            <select
              value={filters.limit}
              onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Cases Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Περιστατικό
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Συγγραφέας
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Τύπος
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Επείγον
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Κατάσταση
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Δραστηριότητα
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ημερομηνία
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ενέργειες
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cases.map((case_) => (
                <tr key={case_.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="max-w-xs">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {case_.title}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {case_.description}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {case_.first_name} {case_.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{case_.email}</div>
                        {case_.hospital_affiliation && (
                          <div className="text-xs text-gray-400">{case_.hospital_affiliation}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {getCaseTypeLabel(case_.case_type)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getUrgencyBadge(case_.urgency_level)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(case_.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Eye className="w-4 h-4 mr-1" />
                        {case_.view_count}
                      </div>
                      <div className="flex items-center">
                        <MessageSquare className="w-4 h-4 mr-1" />
                        {case_.comment_count}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(case_.created_at).toLocaleDateString('el-GR')}
                      </div>
                      {case_.updated_at !== case_.created_at && (
                        <div className="text-xs text-gray-400 mt-1">
                          Ενημ: {new Date(case_.updated_at).toLocaleDateString('el-GR')}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => navigate(`/cases/${case_.id}`)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded"
                        title="Προβολή περιστατικού"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      
                      {case_.status === 'open' && (
                        <button
                          onClick={() => openConfirmModal(case_, 'resolve')}
                          className="text-green-600 hover:text-green-900 p-1 rounded"
                          title="Επίλυση περιστατικού"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      
                      {case_.status !== 'deleted' && (
                        <button
                          onClick={() => openConfirmModal(case_, 'delete')}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Διαγραφή περιστατικού"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.hasPrevious}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Προηγούμενη
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasNext}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Επόμενη
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Εμφάνιση{' '}
                  <span className="font-medium">
                    {((pagination.page - 1) * pagination.limit) + 1}
                  </span>{' '}
                  έως{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  από{' '}
                  <span className="font-medium">{pagination.total}</span> αποτελέσματα
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPrevious}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Προηγούμενη
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const page = Math.max(1, Math.min(
                      pagination.page - 2 + i,
                      pagination.pages - 4 + i
                    ));
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pagination.page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNext}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Επόμενη
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setSelectedCase(null);
          setConfirmConfig({});
        }}
        {...confirmConfig}
      />
    </div>
  );
}